import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Car, Calendar, Clock, CheckCircle2, Circle, XCircle, ArrowLeft } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const LOGO_URL = "/logo.png";

const STATUS_STEPS = [
  { key: "New / Not Contacted", label: "Request Received", progress: 0 },
  { key: "Lead Contacted", label: "Contacted", progress: 20 },
  { key: "Appointment Booked", label: "Appointment Scheduled", progress: 40 },
  { key: "Documents Submitted", label: "Documents Submitted", progress: 60 },
  { key: "Approved", label: "Approved", progress: 80 },
  { key: "Deal Closed / Delivered", label: "Completed", progress: 100 }
];

const ProgressPage = () => {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await axios.get(`${API}/leads/${leadId}/progress`);
        setLead(response.data);
      } catch (err) {
        setError("Unable to find your booking. Please check your link.");
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [leadId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      weekday: "long", 
      year: "numeric", 
      month: "long", 
      day: "numeric" 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getCurrentStepIndex = () => {
    if (!lead) return 0;
    if (lead.status === "Cancelled / Loan Denied") return -1;
    return STATUS_STEPS.findIndex(s => s.key === lead.status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
            Booking Not Found
          </h1>
          <p className="text-[#4A4A4A] mb-6">{error}</p>
          <Link to="/" className="btn-primary inline-block">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const currentStepIndex = getCurrentStepIndex();
  const isCancelled = lead.status === "Cancelled / Loan Denied";
  const isCompleted = lead.status === "Deal Closed / Delivered";

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="progress-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="Trish Drives Honda" className="h-12" />
          </Link>
          <Link to="/" className="text-[#E91E8C] hover:underline flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Greeting */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-2">
            Hi {lead.full_name}!
          </h1>
          <p className="text-lg text-[#4A4A4A]">
            Here's the status of your booking
          </p>
        </div>

        {/* Vehicle Card */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-lg mb-8">
          {lead.vehicle_image && (
            <div className="aspect-video bg-[#FFF5F9] relative overflow-hidden">
              <img 
                src={lead.vehicle_image} 
                alt={lead.vehicle_interested}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                  {lead.vehicle_interested}
                </h2>
              </div>
            </div>
          )}
          
          <div className="p-6 md:p-8">
            {/* Appointment Details */}
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFF5F9] flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#E91E8C]" />
                </div>
                <div>
                  <p className="text-sm text-[#6B5E55]">Appointment Date</p>
                  <p className="font-medium text-[#1A1A1A]">{formatDate(lead.preferred_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFF5F9] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#E91E8C]" />
                </div>
                <div>
                  <p className="text-sm text-[#6B5E55]">Appointment Time</p>
                  <p className="font-medium text-[#1A1A1A]">{formatTime(lead.preferred_time)}</p>
                </div>
              </div>
            </div>

            {/* Status */}
            {isCancelled ? (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-red-800 mb-2">
                  Application Cancelled / Loan Denied
                </h3>
                <p className="text-red-600">
                  Unfortunately, your application could not be processed. Please contact us for more information.
                </p>
              </div>
            ) : isCompleted ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-green-800 mb-2">
                  Congratulations!
                </h3>
                <p className="text-green-600">
                  Your deal has been completed. Thank you for choosing Trish Drives Honda!
                </p>
              </div>
            ) : (
              <>
                {/* Current Status Badge */}
                <div className="flex items-center justify-center gap-3 mb-8">
                  <span className="text-sm text-[#6B5E55]">Current Status:</span>
                  <span className="bg-[#E91E8C] text-white px-4 py-2 rounded-full font-medium">
                    {lead.status}
                  </span>
                </div>

                {/* Progress Steps */}
                <div className="relative">
                  {/* Progress Line */}
                  <div className="absolute top-5 left-5 right-5 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#E91E8C] transition-all duration-500"
                      style={{ width: `${lead.status_progress}%` }}
                    />
                  </div>

                  {/* Steps */}
                  <div className="relative flex justify-between">
                    {STATUS_STEPS.map((step, index) => {
                      const isActive = index <= currentStepIndex;
                      const isCurrent = index === currentStepIndex;
                      
                      return (
                        <div key={step.key} className="flex flex-col items-center" style={{ width: '16.66%' }}>
                          <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                              isActive 
                                ? 'bg-[#E91E8C] text-white' 
                                : 'bg-white border-2 border-gray-300 text-gray-400'
                            } ${isCurrent ? 'ring-4 ring-[#E91E8C]/20' : ''}`}
                          >
                            {isActive ? (
                              <CheckCircle2 className="w-5 h-5" />
                            ) : (
                              <Circle className="w-5 h-5" />
                            )}
                          </div>
                          <p className={`text-xs text-center mt-3 ${
                            isActive ? 'text-[#E91E8C] font-medium' : 'text-gray-400'
                          }`}>
                            {step.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-2xl p-6 text-center">
          <p className="text-[#6B5E55] mb-2">
            Questions about your booking?
          </p>
          <p className="text-[#1A1A1A]">
            Call us at <a href="tel:1876-564-4901" className="text-[#E91E8C] font-medium hover:underline">1876-564-4901</a> or email{" "}
            <a href="mailto:twong@atlautomotive.com" className="text-[#E91E8C] font-medium hover:underline">twong@atlautomotive.com</a>
          </p>
        </div>

        {/* Last Updated */}
        <p className="text-center text-sm text-[#6B5E55] mt-6">
          Last updated: {new Date(lead.updated_at).toLocaleString()}
        </p>
      </main>
    </div>
  );
};

export default ProgressPage;
